import { addBreadcrumb as addSentryBreadcrumb } from '../sentry';
import type { Breadcrumb } from '../types';

/**
 * Add a breadcrumb for tracking user actions and events
 */
export function addBreadcrumb(breadcrumb: Breadcrumb): void {
  addSentryBreadcrumb(breadcrumb);
}

/**
 * Add a navigation breadcrumb
 */
export function addNavigationBreadcrumb(from: string, to: string): void {
  addBreadcrumb({
    category: 'navigation',
    message: `Navigated from ${from} to ${to}`,
    level: 'info',
    data: { from, to },
  });
}

/**
 * Add an HTTP request breadcrumb
 */
export function addHttpBreadcrumb(
  method: string,
  url: string,
  statusCode?: number,
  isError: boolean = false
): void {
  addBreadcrumb({
    category: 'http',
    message: `${method.toUpperCase()} ${url}${statusCode ? ` [${statusCode}]` : ''}`,
    level: isError ? 'error' : 'info',
    data: {
      method: method.toUpperCase(),
      url,
      statusCode,
    },
  });
}

/**
 * Add a user action breadcrumb
 */
export function addUserActionBreadcrumb(
  action: string,
  target?: string,
  data?: Record<string, unknown>
): void {
  addBreadcrumb({
    category: 'user-action',
    message: target ? `${action}: ${target}` : action,
    level: 'info',
    data,
  });
}

/**
 * Add a UI event breadcrumb
 */
export function addUIBreadcrumb(
  eventType: 'click' | 'input' | 'submit' | 'change' | 'focus' | 'blur',
  element: string,
  data?: Record<string, unknown>
): void {
  addBreadcrumb({
    category: 'ui',
    message: `${eventType} on ${element}`,
    level: 'info',
    data,
  });
}

/**
 * Add a console breadcrumb (useful for tracking console.error calls)
 */
export function addConsoleBreadcrumb(
  level: 'log' | 'info' | 'warn' | 'error',
  message: string,
  args?: unknown[]
): void {
  addBreadcrumb({
    category: 'console',
    message,
    level: level === 'error' ? 'error' : level === 'warn' ? 'warning' : 'info',
    data: args ? { arguments: args } : undefined,
  });
}

/**
 * Add an app lifecycle breadcrumb
 */
export function addLifecycleBreadcrumb(
  event: 'mount' | 'unmount' | 'update' | 'error' | 'suspend' | 'resume',
  component?: string,
  data?: Record<string, unknown>
): void {
  addBreadcrumb({
    category: 'app-lifecycle',
    message: component ? `${event}: ${component}` : event,
    level: event === 'error' ? 'error' : 'info',
    data,
  });
}
